import type { SportCode } from '~/shared/api/sports-api';
import type { PlanTournamentBody } from '~/shared/api/generated';

export const queryKeys = {
  sports: {
    all: (): ['sports'] => ['sports'],
  },

  gameInvites: {
    all: (): ['game-invites'] => ['game-invites'],
    club: (kind: string, subjectId: string): ['game-invites', 'club', string, string] => [
      'game-invites',
      'club',
      kind,
      subjectId,
    ],
  },

  news: {
    all: (): ['news'] => ['news'],
    list: (status: string): ['news', 'list', string] => ['news', 'list', status],
    detail: (id: string): ['news', 'detail', string] => ['news', 'detail', id],
  },

  pos: {
    all: (): ['pos'] => ['pos'],
    currentShift: (): ['pos', 'current-shift'] => ['pos', 'current-shift'],
    unfiscalised: (): ['pos', 'unfiscalised'] => ['pos', 'unfiscalised'],
    receiptForm: (): ['pos', 'receipt-form'] => ['pos', 'receipt-form'],
  },

  schedule: {
    all: (): ['schedule'] => ['schedule'],
    detail: (
      date: string,
      sport?: SportCode | null,
      includeCancelled = false,
    ): ['schedule', string, SportCode | null | undefined, boolean] =>
      ['schedule', date, sport, includeCancelled],
  },

  scheduleRange: {
    all: (): ['schedule-range'] => ['schedule-range'],
    detail: (from: string, to: string, sport?: SportCode | null): ['schedule-range', string, string, SportCode | null | undefined] =>
      ['schedule-range', from, to, sport],
  },

  dashboardLayout: {
    all: (): ['dashboard', 'layout'] => ['dashboard', 'layout'],
  },

  tournament: {
    all: (): ['tournaments', 'list'] => ['tournaments', 'list'],
    detail: (id: string): ['tournament', string] => ['tournament', id],
    plans: (): ['tournaments', 'plan'] => ['tournaments', 'plan'],
    plan: (body: PlanTournamentBody | null): ['tournaments', 'plan', PlanTournamentBody | null] => [
      'tournaments',
      'plan',
      body,
    ],
    norms: (): ['tournaments', 'norms'] => ['tournaments', 'norms'],
  },

  notifications: {
    all: (): ['notifications'] => ['notifications'],
    list: (): ['notifications', 'list'] => ['notifications', 'list'],
    unreadCount: (): ['notifications', 'unread-count'] => ['notifications', 'unread-count'],
  },

  chat: {
    all: (): ['chat'] => ['chat'],
    conversations: (): ['chat', 'conversations'] => ['chat', 'conversations'],
    oversight: (trainerId: string | null, clientId: string | null): ['chat', 'oversight', string, string] => [
      'chat',
      'oversight',
      trainerId ?? '',
      clientId ?? '',
    ],
    oversightMessages: (id: string): ['chat', 'oversight-messages', string] => ['chat', 'oversight-messages', id],
    oversightReads: (id: string): ['chat', 'oversight-reads', string] => ['chat', 'oversight-reads', id],
    messages: (id: string): ['chat', 'messages', string] => ['chat', 'messages', id],
    rooms: (): ['chat', 'rooms'] => ['chat', 'rooms'],
    roomMessages: (kind: string, subjectId: string): ['chat', 'room', string, string] => ['chat', 'room', kind, subjectId],
  },

  accessZones: {
    all: (): ['access', 'zones'] => ['access', 'zones'],
  },

  resources: {
    all: (): ['resources'] => ['resources'],
    options: (): ['resources', 'options'] => ['resources', 'options'],
    places: (): ['resources', 'places'] => ['resources', 'places'],
  },

  club: {
    profile: (): ['settings', 'club', 'profile'] => ['settings', 'club', 'profile'],
  },

  platformTenants: {
    all: (): ['platform', 'tenants'] => ['platform', 'tenants'],
    details: (): ['platform', 'tenants', 'detail'] => ['platform', 'tenants', 'detail'],
    detail: (id: string): ['platform', 'tenants', 'detail', string] => ['platform', 'tenants', 'detail', id],
  },

  groupSessions: {
    fitnessAll: (): ['group-sessions', 'fitness'] => ['group-sessions', 'fitness'],
    fitness: (from: string, to: string): ['group-sessions', 'fitness', string, string] => [
      'group-sessions',
      'fitness',
      from,
      to,
    ],
    fitnessNoShows: (
      date: string,
      sessionIds: readonly string[],
    ): ['group-sessions', 'fitness', 'no-shows', string, readonly string[]] => [
      'group-sessions',
      'fitness',
      'no-shows',
      date,
      sessionIds,
    ],
  },

  settings: {
    friendsPolicy: (): ['settings', 'friends-policy'] => ['settings', 'friends-policy'],
  },
} as const;
